import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { AxiosResponse } from 'axios';

import {
  getHomeOwnerWorldApi,
  getLeadDistributionApi,
  getPropertyValuationApi,
} from '@/helpers/Api';
import { getAxiosClient } from '@/helpers/Axios/axiosClient';
import { logAxiosError } from '@/helpers/RUMService/RUMService';
import { BookQualifiedPropertyValuationResponse } from '@/models/mutations/bookQualifiedPropertyValuation';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { GetPropertyDetailsResponse, PropertyDetails } from '@/models/queries/getPropertyDetails';
import { OfferType } from '@/models/valuationFunnel';
import { ARTICLE_API_PATH } from '@/router/routes/constants';
import type { Article } from '@/stores/types/landing';
import {
  Address,
  HomeOwnerWorldProperty,
  HomeOwnerWorldPropertyCreateRequest,
  HomeOwnerWorldPropertyFromValuationFunnelRequest,
  HomeOwnerWorldValuationFormData,
  Lead,
  Location,
  PostQualifiedValuationResult,
  PropertyCommonDataResponse,
  PropertyValuationData,
} from '@/stores/types/propertyValuation';
import { HttpStatusCode, Pages } from '@/types';
import { Intent } from '@/types/intent';
import { captureError } from '@smg-real-estate/browser-rum';

export function getGeometryLocation(obj: google.maps.LatLngLiteral | google.maps.LatLng) {
  return {
    lat: typeof obj.lat === 'function' ? obj.lat() : obj.lat,
    lon: typeof obj.lng === 'function' ? obj.lng() : obj.lng,
  };
}

export type AddressComponent =
  | 'streetName'
  | 'streetNumber'
  | 'city'
  | 'postalCode'
  | 'countryName';

/**
 * Converts Google Places result to an address object
 *
 * @param placeResult - Google Places API result
 * @param formattedAddressFields - Controls which components are included in the formattedAddress.
 *        Uses standard formatting: "Main St 123, 8000 Zurich, Switzerland" (streetName streetNumber, postalCode city, countryName)
 *
 * @example
 * // Full address: "Main St 123, 8000 Zurich, Switzerland"
 * convertLocationObject(placeResult);
 *
 * @example
 * // Only street: "Main St 123"
 * convertLocationObject(placeResult, ['streetName', 'streetNumber']);
 *
 * @example
 * // City and postal code: "8000 Zurich"
 * convertLocationObject(placeResult, ['postalCode', 'city']);
 */
export function convertLocationObject(
  placeResult: google.maps.places.PlaceResult = {},
  formattedAddressFields: AddressComponent[] = [
    'city',
    'countryName',
    'postalCode',
    'streetName',
    'streetNumber',
  ],
): Location {
  if (!placeResult.address_components) return {};

  // 1. Extract address components
  const addressObj = placeResult.address_components.reduce(
    (acc, component) => {
      if (component.types.includes('locality')) acc.city = component.long_name;
      if (component.types.includes('country')) {
        acc.countryCode = component.short_name;
        acc.countryName = component.long_name;
      }
      if (
        component.types.includes('postal_code') ||
        component.types.includes('postal_code_prefix')
      ) {
        acc.postalCode = component.long_name;
      }
      if (component.types.includes('route')) acc.streetName = component.long_name;
      if (component.types.includes('street_number')) acc.streetNumber = component.long_name;
      return acc;
    },
    {} as Record<string, string>,
  );

  // 2. Street
  const street = [addressObj.streetName, addressObj.streetNumber].filter(Boolean).join(' ');

  // 3. Lat & Lng
  const coordinates = placeResult.geometry?.location
    ? getGeometryLocation(placeResult.geometry.location)
    : {};

  // 4. Parse formatted address based on requested fields
  const addressParts = [
    // Street
    [
      formattedAddressFields.includes('streetName') && addressObj.streetName,
      formattedAddressFields.includes('streetNumber') && addressObj.streetNumber,
    ]
      .filter(Boolean)
      .join(' '),

    // Locality
    [
      formattedAddressFields.includes('postalCode') && addressObj.postalCode,
      formattedAddressFields.includes('city') && addressObj.city,
    ]
      .filter(Boolean)
      .join(' '),

    // Country
    formattedAddressFields.includes('countryName') && addressObj.countryName,
  ].filter(Boolean);

  return {
    streetName: addressObj.streetName,
    streetNumber: addressObj.streetNumber,
    city: addressObj.city,
    postalCode: addressObj.postalCode,
    countryCode: addressObj.countryCode,
    street,
    ...coordinates,
    formattedAddress: addressParts.join(', '),
  };
}

/**
 * API Gateway won't perform type-checking if the optional fields are not presence
 * This function will remove fields have null value
 */
export function truncateNullFields<T>(payload: T): T {
  if (!payload) {
    return payload;
  }

  return Object.keys(payload).reduce((newObject, key) => {
    if (typeof payload[key] !== 'object' || payload[key] !== null) {
      newObject[key] = payload[key];
    }
    return newObject;
  }, {} as T);
}

export async function postQualifiedValuation(
  values: PropertyValuationData,
): PostQualifiedValuationResult {
  const apiName = 'valuation-request';
  const truncatedValues = truncateNullFields(values);
  try {
    const response: AxiosResponse<BookQualifiedPropertyValuationResponse> =
      await getAxiosClient().post(getPropertyValuationApi(apiName), truncatedValues);
    const { status, data } = response;

    const isSuccess = status === HttpStatusCode.Created;
    return {
      isSuccess,
      result: data,
    };
  } catch (e) {
    logAxiosError(e, {
      endpoint: apiName,
      requestData: truncatedValues,
      feature: Pages.Funnel,
    });
    return {
      isSuccess: false,
      result: null,
      statusCode: e.response?.status,
    };
  }
}

export async function postAuthorisedTokenValuation(
  values: HomeOwnerWorldValuationFormData,
): Promise<{ isCreated: boolean; leadId: string }> {
  try {
    const requestPayload = mapFormDataToHomeOwnerWorldProperty(values);

    const response = await getAxiosClient().post(
      getHomeOwnerWorldApi('properties'),
      truncateNullFields(requestPayload),
    );
    const mappedResponseData = mapHomeOwnerWorldPropertyToPropertyValuationResult(response.data);
    return {
      isCreated: response.status === HttpStatusCode.Created,
      leadId: mappedResponseData.leadId ?? '',
    };
  } catch (e) {
    captureError(e);
    return { isCreated: false, leadId: '' };
  }
}

export async function verifyOTP(values: {
  leadId: string;
  otpCode: string;
}): Promise<{ isSuccess: boolean }> {
  const apiName = 'verify-otp';
  try {
    const response: AxiosResponse<BookQualifiedPropertyValuationResponse> =
      await getAxiosClient().put(getPropertyValuationApi(apiName), values);

    const isSuccess = response.status === HttpStatusCode.OK;
    return {
      isSuccess,
    };
  } catch (e) {
    logAxiosError(e, {
      endpoint: apiName,
      requestData: values,
      feature: Pages.Funnel,
    });
    return {
      isSuccess: false,
    };
  }
}

export async function putLead(lead: Lead) {
  try {
    const response: AxiosResponse = await getAxiosClient().put(
      getLeadDistributionApi('leads/homegate'),
      lead,
    );
    return response.status === HttpStatusCode.Accepted;
  } catch (e) {
    captureError(e);
    return false;
  }
}

export async function fetchPropertyDetails({
  lat,
  lng,
  locale = HgSupportedLanguage.DE,
  street,
  postalCode,
  city,
}: {
  lat?: string | number;
  lng?: string | number;
  street?: string;
  postalCode?: string;
  city?: string;
  locale?: HgSupportedLanguage;
}): Promise<Array<PropertyDetails>> {
  try {
    const cultureCode = `${locale}-CH`;
    const response: AxiosResponse<GetPropertyDetailsResponse> = await getAxiosClient().get(
      getPropertyValuationApi('v2/property-detail-data'),
      {
        params: {
          lat,
          lon: lng,
          cultureCode,
          street,
          postalCode,
          city,
        },
      },
    );
    return response.data ?? [];
  } catch (e) {
    captureError(e);
    return [];
  }
}

export async function fetchPropertyDetailsHow({
  lat,
  lng,
  locale = HgSupportedLanguage.DE,
  street,
  postalCode,
  city,
}: {
  lat?: string | number;
  lng?: string | number;
  street?: string;
  postalCode?: string;
  city?: string;
  locale?: HgSupportedLanguage;
}): Promise<PropertyCommonDataResponse> {
  try {
    const cultureCode = `${locale}-CH`;
    const response: AxiosResponse<PropertyCommonDataResponse> = await getAxiosClient().get(
      getHomeOwnerWorldApi('property-detail-data'),
      {
        params: {
          lat,
          lon: lng,
          cultureCode,
          street,
          postalCode,
          city,
        },
      },
    );
    return response.data ?? { items: [] };
  } catch (e) {
    captureError(e);
    return { items: [] };
  }
}

export async function fetchArticles(
  basePath: string,
  language: HgSupportedLanguage,
): Promise<Article[]> {
  try {
    const response: AxiosResponse<Article[]> = await getAxiosClient().get(
      `${basePath}${ARTICLE_API_PATH(language)}`,
    );
    return response.data;
  } catch (e) {
    captureError(e);
    return [];
  }
}

export async function postSubscribeToNewsletter(
  email: string,
  language: HgSupportedLanguage,
): Promise<void> {
  try {
    await getAxiosClient().post(`${getPropertyValuationApi('subscribe-newsletter')}`, {
      email: encodeURIComponent(email),
      language,
    });
  } catch (e) {
    captureError(e);
  }
}

export function mapLocationToAddress(location: Location): Address {
  return {
    lat: location.lat ?? 0,
    lon: location.lon ?? 0,
    countryCode: location.countryCode ?? '',
    postalCode: location.postalCode ?? '',
    city: location.city ?? '',
    street: location.street ?? '',
  };
}

export function normalizeFunnelAddress(location?: Location | null): Address {
  if (!location) {
    return {
      lat: 0,
      lon: 0,
      countryCode: 'CH',
      postalCode: '',
      city: '',
      street: '',
    };
  }

  const mapped = mapLocationToAddress(location);
  const streetFromParts = [location.streetName, location.streetNumber]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    ...mapped,
    street: mapped.street || streetFromParts,
    countryCode: mapped.countryCode || 'CH',
  };
}

export function mapOfferTypeToIntent(offerType?: OfferType): Intent | undefined {
  if (offerType === undefined) {
    return undefined;
  }

  switch (offerType) {
    case OfferType.Selling:
      return Intent.Selling;
    case OfferType.Buying:
      return Intent.Buying;
    case OfferType.Own:
    case OfferType.Other:
      return Intent.Inform;
    default:
      return undefined;
  }
}

export function mapFormDataToHomeOwnerWorldPropertyFromValuationFunnel(
  formData: PropertyValuationData,
  culture: string,
  selectedPropertyDetail?: PropertyDetails,
): HomeOwnerWorldPropertyFromValuationFunnelRequest {
  const address = normalizeFunnelAddress(formData.address);
  const intent = mapOfferTypeToIntent(formData.offerType);

  return {
    address,
    propertyType: formData.propertyType!,
    livingSpace: formData.livingSpace,
    room: formData.rooms,
    yearBuild: formData.buildYear,
    floor: selectedPropertyDetail?.floor,
    lotSize: formData.lotSize ?? undefined,
    renovationYear: formData.renovationYear ?? undefined,
    isRenovated: formData.isRenovated,
    culture,
    ...(intent && { intent }),
  };
}

export function mapFormDataToHomeOwnerWorldProperty(
  propertyValuationData: HomeOwnerWorldValuationFormData,
): HomeOwnerWorldPropertyCreateRequest {
  return {
    offerType: propertyValuationData.offerType ?? 0,
    lotSize: propertyValuationData.lotSize ?? undefined,
    renovationYear: propertyValuationData.renovationYear ?? undefined,
    whenToSell: propertyValuationData.whenToSell,
    address: mapLocationToAddress(propertyValuationData.address),
    propertyType: propertyValuationData.propertyType as any,
    livingSpace: propertyValuationData.livingSpace ?? 0,
    culture: propertyValuationData.culture ?? 'de-CH',
    room: propertyValuationData.rooms ?? 0,
    yearBuild: propertyValuationData.buildYear ?? 1900,
    isRenovated: propertyValuationData.isRenovated ?? false,
    phoneNumber: propertyValuationData.phoneNumber ?? undefined,
    isConsultantRequested: propertyValuationData.isConsultantRequested ?? undefined,
  };
}

export const mapHomeOwnerWorldPropertyToPropertyValuationResult = (
  howProperty: HomeOwnerWorldProperty,
): PropertyValuationResult =>
  ({
    leadId: howProperty.propertyId,
    town: howProperty.address?.city,
    zipCode: howProperty.address?.postalCode,
    street: howProperty.address?.street,
    lat: howProperty.address.lat,
    lon: howProperty.address.lon,
    municipalityId: String(howProperty.municipalityId),
    municipalityLabel: howProperty.municipalityLabel,
    municipalityPriceDevelopment: howProperty.municipalityPriceDevelopment,
    municipalityPopulationGrowth: howProperty.municipalityPopulationGrowth,
    municipalityResidentialConstruction: howProperty.municipalityResidentialConstruction,
    municipalityVacancy: howProperty.municipalityVacancy,
    municipalityTax: howProperty.municipalityTax,
    propertyType: howProperty.propertyType,
    offerType: howProperty.offerType,
    livingSpace: howProperty.livingSpace,
    room: howProperty.room,
    lotSize: howProperty.lotSize,
    yearBuild: howProperty.yearBuild,
    isRenovated: howProperty.isRenovated,
    renovationYear: howProperty.renovationYear,
    evaluatedPriceFrom: howProperty.evaluatedPriceFrom,
    evaluatedPriceTo: howProperty.evaluatedPriceTo,
    appraisalPrice: howProperty.appraisalPrice,
    appraisalDate: howProperty.appraisalDate,
    createdDate: howProperty.createdDate,
    estimatedOfferedRent: howProperty.estimatedOfferedRent,
    priceEvolution: howProperty.priceEvolution,
    whenToSell: howProperty.whenToSell ?? undefined,
    isConsultantRequested: howProperty.isConsultantRequested ?? undefined,
    phoneNumber: howProperty.phoneNumber ?? undefined,
    intent: howProperty.intent as Intent | undefined,
    revaluationTriggered: howProperty.revaluationTriggered,
    remainingValuations: howProperty.remainingValuations,
  }) as PropertyValuationResult;
